import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";

import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import CampaignMessageComposer from "../CampaignMessageComposer";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import AttachmentPreview from "../AttachmentPreview";

const useStyles = makeStyles((theme) => ({
    // ✅ ESTILOS PRINCIPALES
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    
    // ✅ CORREGIDO: DialogTitle dinámico según tema
    dialogTitle: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        padding: theme.spacing(2, 2, 1.5),
        display: 'flex',
        alignItems: 'flex-start',
        borderBottom: `1px solid ${theme.mode === 'dark' ? '#334155' : '#eef0f4'}`,
    },
    
    dialogTitleIcon: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
        flexShrink: 0,
        '& svg': {
            fontSize: 20,
        },
    },
    dialogTitleCopy: {
        flex: 1,
        minWidth: 0,
    },
    dialogTitleText: {
        fontSize: 16,
        fontWeight: 700,
        lineHeight: 1.3,
    },
    dialogTitleHint: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 2,
        fontWeight: 400,
    },
    fieldLabel: {
        fontSize: 13,
        fontWeight: 600,
        marginBottom: 6,
        color: theme.palette.text.primary,
    },
    requiredMark: {
        color: theme.palette.error.main,
        marginLeft: 2,
    },
    
    // ✅ CORREGIDO: DialogContent dinámico según tema
    dialogContent: {
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
    },
    
    formSection: {
        marginBottom: theme.spacing(3),
    },
    
    // ✅ CORREGIDO: SectionTitle dinámico según tema
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    
    sectionIcon: {
        marginRight: theme.spacing(1),
        fontSize: '1.2rem',
    },
    
    // ✅ ESTILOS PARA CAMPOS
    fieldContainer: {
        marginBottom: theme.spacing(2),
    },
    
    shortcodeField: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
            fontSize: 14,
        },
    },
    
    // ✅ CORREGIDO: VariablesSection dinámico según tema
    variablesSection: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
        marginTop: theme.spacing(2),
        boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    },
    
    // ✅ CORREGIDO: VariablesTitle dinámico según tema
    variablesTitle: {
        fontSize: '0.875rem',
        fontWeight: 'bold',
        color: theme.palette.text.secondary || theme.palette.text.primary,
        marginBottom: theme.spacing(1),
    },
    
    // ✅ CORREGIDO: VariableChip dinámico según tema
    variableChip: {
        margin: theme.spacing(0.5),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 'bold',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark || '#1565c0',
        },
    },
    
    // ✅ CORREGIDO: FileSection dinámico según tema
    fileSection: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
        marginTop: theme.spacing(2),
        boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
    },
    
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    
    // ✅ CORREGIDO: FileIcon dinámico según tema
    fileIcon: {
        color: theme.palette.primary.main,
    },
    
    deleteFileButton: {
        color: '#d32f2f',
        backgroundColor: 'rgba(211, 47, 47, 0.1)',
        borderRadius: '50%',
        padding: '4px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: '#d32f2f',
            color: 'white',
            transform: 'scale(1.1)',
            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
        },
        '&:active': {
            transform: 'scale(0.95)',
        },
    },
    
    // ✅ ESTILOS PARA BOTONES
    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    
    // ✅ CORREGIDO: DialogActions dinámico según tema
    dialogActions: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
    },
    
    // ✅ CORREGIDO: AttachButton dinámico según tema
    attachButton: {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        backgroundColor: theme.mode === 'dark' ? 'transparent' : 'white',
        borderRadius: '8px',
        padding: '8px 16px',
        fontWeight: 'bold',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)'}`,
        },
        '&:active': {
            transform: 'translateY(0)',
        },
    },
    
    // ✅ CORREGIDO: CancelButton dinámico según tema
    cancelButton: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 14,
        color: theme.palette.primary.main,
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#eef2ff',
        },
    },
    
    // ✅ CORREGIDO: SaveButton dinámico según tema
    saveButton: {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        borderRadius: 999,
        padding: '6px 18px',
        fontWeight: 700,
        fontSize: 14,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark || '#1565c0',
        },
    },
    
    // ✅ CORREGIDO: TextField dinámico según tema
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '&:hover': {
                backgroundColor: theme.palette.background.paper,
            },
            '&.Mui-focused': {
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 0 0 2px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`,
            },
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.palette.primary.main,
        },
        "& .MuiFormHelperText-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
    },
    
    // ✅ CORREGIDO: FormControl dinámico según tema
    formControl: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
            color: theme.palette.text.primary,
            '&:hover': {
                backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
            },
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
            "&.Mui-focused": {
                color: theme.palette.primary.main,
            },
        },
        "& .MuiSelect-root": {
            color: theme.palette.text.primary,
        },
        "& .MuiMenuItem-root": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            "&:hover": {
                backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
            },
        },
    },
}));

const QuickeMessageSchema = Yup.object().shape({
    shortcode: Yup.string().required("Obligatorio"),
    //   message: Yup.string().required("Obligatorio"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload, resetPagination }) => {
    const classes = useStyles();
    const theme = useTheme();
    const { user } = useContext(AuthContext);
    const { profile } = user;

    const initialState = {
        shortcode: "",
        message: "",
        geral: false,
        status: true,
    };

    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [quickemessage, setQuickemessage] = useState(initialState);
    const [attachment, setAttachment] = useState(null);
    const attachmentFile = useRef(null);

    const refreshList = () => {
        if (typeof reload === "function") reload();
        else if (typeof resetPagination === "function") resetPagination();
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!open) return;
            if (!quickemessageId) {
                setQuickemessage(initialState);
                setAttachment(null);
                return;
            }
            try {
                const { data } = await api.get(`/quick-messages/${quickemessageId}`);
                if (cancelled) return;
                setQuickemessage((prevState) => ({ ...prevState, ...data }));
            } catch (err) {
                toastError(err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [quickemessageId, open]);

    const handleClose = () => {
        setQuickemessage(initialState);
        setAttachment(null);
        onClose();
    };

    const handleAttachmentFile = (e) => {
      
        const file = head(e.target.files);
        if (file) {
            setAttachment(file);
        }
    };

    const handleSaveQuickeMessage = async (values) => {
        // ✅ VALIDACIÓN: Asegurar que el campo message esté presente
        if (!values.message || values.message.trim() === '') {
            toast.error("El campo 'Respuesta' es obligatorio");
            return;
        }

        // ✅ Helper function para extraer nombre de archivo de URL/ruta (compatible con navegador)
        const getFileName = (filePath) => {
            if (!filePath) return null;
            // Manejar tanto rutas Unix (/) como Windows (\) y URLs
            return filePath.split('/').pop().split('\\').pop();
        };

        const hasMedia = Boolean(attachment || values.mediaPath);
        const quickemessageData = { 
            ...values, 
            shortcode: String(values.shortcode || "").trim(),
            message: values.message.trim(),
            isMedia: hasMedia, 
            mediaPath: attachment ? String(attachment.name).replace(/ /g, "_") : values.mediaPath ? getFileName(values.mediaPath).replace(/ /g, "_") : null 
        };

        try {
            if (quickemessageId) {
                await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
                if (attachment != null) {
                    const formData = new FormData();
                    formData.append("typeArch", "quickMessage");
                    formData.append("file", attachment);
                    await api.post(
                        `/quick-messages/${quickemessageId}/media-upload`,
                        formData
                    );
                }
            } else {
                const { data } = await api.post("/quick-messages", quickemessageData);
                if (attachment != null) {
                    const formData = new FormData();
                    formData.append("typeArch", "quickMessage");
                    formData.append("file", attachment);
                    await api.post(`/quick-messages/${data.id}/media-upload`, formData);
                }
            }
            toast.success(i18n.t("quickMessages.toasts.success"));
            refreshList();
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const deleteMedia = async () => {
        if (attachment) {
            setAttachment(null);
            attachmentFile.current.value = null;
        }

        if (quickemessage.mediaPath) {
            const mediaId = quickemessage.id || quickemessageId;
            if (!mediaId) return;
            try {
                await api.delete(`/quick-messages/${mediaId}/media-upload`);
                setQuickemessage((prev) => ({
                    ...prev,
                    mediaPath: null,
                    mediaName: null,
                }));
                toast.success("Archivo adjunto eliminado");
                refreshList();
            } catch (err) {
                toastError(err);
            }
        }
    };

    return (
        <div className={classes.root}>
            <ConfirmationModal
                title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                onConfirm={deleteMedia}
            >
                {i18n.t("quickMessages.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{
                    style: {
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: '12px',
                        boxShadow: theme.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <span className={classes.dialogTitleIcon}>
                        <FlashOnIcon />
                    </span>
                    <div className={classes.dialogTitleCopy}>
                        <Typography className={classes.dialogTitleText}>
                            {quickemessageId ? "Editar respuesta rápida" : "Añadir respuesta rápida"}
                        </Typography>
                        <Typography className={classes.dialogTitleHint}>
                            Escribe “/” en el chat para insertar este mensaje.
                        </Typography>
                    </div>
                    <IconButton size="small" onClick={handleClose} aria-label="Cerrar">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                
                <div style={{ display: "none" }}>
                    <input
                        type="file"
                        ref={attachmentFile}
                        onChange={(e) => handleAttachmentFile(e)}
                    />
                </div>
                
                <Formik
                    initialValues={quickemessage}
                    enableReinitialize={true}
                    validationSchema={QuickeMessageSchema}
                    onSubmit={async (values, actions) => {
                        try {
                            await handleSaveQuickeMessage(values);
                        } finally {
                            actions.setSubmitting(false);
                        }
                    }}
                >
                    {({ touched, errors, isSubmitting, setFieldValue, values }) => {
                        return (
                        <Form>
                            <DialogContent className={classes.dialogContent}>
                                <div className={classes.formSection}>
                                    <div className={classes.fieldContainer}>
                                        <Typography className={classes.fieldLabel}>
                                            Atajo<span className={classes.requiredMark}>*</span>
                                        </Typography>
                                        <TextField
                                            autoFocus
                                            name="shortcode"
                                            value={values.shortcode || ""}
                                            onChange={(e) => setFieldValue('shortcode', e.target.value)}
                                            error={touched.shortcode && Boolean(errors.shortcode)}
                                            helperText={touched.shortcode && errors.shortcode}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            className={`${classes.shortcodeField} ${classes.textField}`}
                                            placeholder="Ej.: bienvenida"
                                        />
                                    </div>
                                    
                                    {(profile === "admin") && (
                                        <div className={classes.fieldContainer}>
                                            <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                                                <InputLabel id="geral-selection-label">
                                                    Disponible para todos
                                                </InputLabel>
                                                <Select
                                                    label="Disponible para todos"
                                                    placeholder="Disponible para todos"
                                                    labelId="geral-selection-label"
                                                    id="geral"
                                                    name="geral"
                                                    value={values.geral || false}
                                                    onChange={(e) => setFieldValue('geral', e.target.value)}
                                                    error={touched.geral && Boolean(errors.geral)}
                                                >
                                                    <MenuItem value={true}>Sí</MenuItem>
                                                    <MenuItem value={false}>Solo mi usuario</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                    )}
                                </div>
                                
                                <div className={classes.formSection}>
                                    <Typography className={classes.fieldLabel}>
                                        Mensaje<span className={classes.requiredMark}>*</span>
                                    </Typography>
                                    <CampaignMessageComposer
                                        value={values.message || ""}
                                        onChange={(next) => setFieldValue("message", next)}
                                        disabled={isSubmitting}
                                        error={touched.message && Boolean(errors.message)}
                                        helperText={touched.message && errors.message}
                                        placeholder="Escribe el mensaje que se enviará"
                                        rows={6}
                                        maxLength={4000}
                                        showCounter={false}
                                        showAttach={!attachment && !quickemessage.mediaPath}
                                        onAttach={() => attachmentFile.current && attachmentFile.current.click()}
                                        attachDisabled={isSubmitting}
                                    />
                                </div>
                                
                                {(quickemessage.mediaPath || attachment) && (
                                    <Paper className={classes.fileSection}>
                                        <Typography className={classes.sectionTitle}>
                                            <AttachFileIcon className={classes.sectionIcon} />
                                            Archivo Adjunto
                                        </Typography>
                                        <AttachmentPreview
                                            file={attachment}
                                            mediaPath={quickemessage.mediaPath}
                                            mediaName={attachment ? attachment.name : quickemessage.mediaName}
                                            onDelete={() => setConfirmationOpen(true)}
                                        />
                                    </Paper>
                                )}
                            </DialogContent>
                            
                            <DialogActions className={classes.dialogActions}>
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className={classes.cancelButton}
                                >
                                    Cancelar
                                </Button>
                                
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={`${classes.btnWrapper} ${classes.saveButton}`}
                                >
                                    {quickemessageId ? "Guardar" : "Agregar"}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                        );
                    }}
                </Formik>
            </Dialog>
        </div>
    );
};

export default QuickMessageDialog;
